import { useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, ImagePlus, Loader2, X } from "lucide-react";

const CATEGORIES = ["community", "medical", "education", "creative", "emergency", "memorial", "business", "animals"] as const;
type Category = (typeof CATEGORIES)[number];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const TITLE_MIN = 5;
const TITLE_MAX = 120;
const STORY_MIN = 30;
const STORY_MAX = 2000;
const GOAL_MIN_USD = 1;
const GOAL_MAX_USD = 1_000_000;
const DEADLINE_MIN_DAYS = 1;
const DEADLINE_MAX_DAYS = 180;

const startOfToday = () => { const d = new Date(); d.setHours(0,0,0,0); return d; };
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate()+n); return x; };
const fmtDate = (d: Date) => d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

export default function IsleFundNew() {
  const { user } = useAuth();
  const nav = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string>("");
  const [form, setForm] = useState({
    title: "",
    story: "",
    category: "community",
    goal: "",
    deadline: "",
  });

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    if (f.size > MAX_IMAGE_BYTES) { toast.error("Image must be under 5 MB"); return; }
    setCoverFile(f);
    setCoverPreview(URL.createObjectURL(f));
  };

  const clearCover = () => {
    if (coverPreview) URL.revokeObjectURL(coverPreview);
    setCoverFile(null);
    setCoverPreview("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    const title = form.title.trim();
    const story = form.story.trim();

    if (title.length < TITLE_MIN) {
      toast.error("Title is too short", { description: `Use at least ${TITLE_MIN} characters so backers know what you're raising for.` });
      return;
    }
    if (title.length > TITLE_MAX) {
      toast.error("Title is too long", { description: `Keep it under ${TITLE_MAX} characters.` });
      return;
    }
    if (story.length < STORY_MIN) {
      toast.error("Add a bit more to your story", { description: `Tell backers why this matters — at least ${STORY_MIN} characters.` });
      return;
    }
    if (story.length > STORY_MAX) {
      toast.error("Story is too long", { description: `Trim it to ${STORY_MAX} characters or less.` });
      return;
    }
    if (!CATEGORIES.includes(form.category as Category)) {
      toast.error("Pick a category", { description: "Choose the option that best fits your campaign." });
      return;
    }

    const goalNum = parseFloat(form.goal);
    if (!Number.isFinite(goalNum) || goalNum <= 0) {
      toast.error("Enter a valid goal", { description: "The amount must be a positive number." });
      return;
    }
    if (goalNum < GOAL_MIN_USD) {
      toast.error("Goal is too small", { description: `Minimum goal is $${GOAL_MIN_USD}.` });
      return;
    }
    if (goalNum > GOAL_MAX_USD) {
      toast.error("Goal is too large", { description: `Maximum goal is $${GOAL_MAX_USD.toLocaleString()}.` });
      return;
    }
    const goalCents = Math.round(goalNum * 100);

    if (form.deadline) {
      const picked = new Date(form.deadline);
      if (Number.isNaN(picked.getTime())) {
        toast.error("That date doesn't look right", { description: "Pick a date from the calendar." });
        return;
      }
      const today = startOfToday();
      const earliest = addDays(today, DEADLINE_MIN_DAYS);
      const latest = addDays(today, DEADLINE_MAX_DAYS);
      if (picked < earliest) {
        toast.error("Deadline is too soon", { description: `Give backers time — choose a date on or after ${fmtDate(earliest)}.` });
        return;
      }
      if (picked > latest) {
        toast.error("Deadline is too far out", { description: `Campaigns can run up to ${DEADLINE_MAX_DAYS} days. Latest is ${fmtDate(latest)}.` });
        return;
      }
    }

    setBusy(true);
    let coverUrl: string | null = null;

    if (coverFile) {
      setUploading(true);
      const ext = coverFile.name.split(".").pop()?.toLowerCase() || "jpg";
      const path = `${user.id}/${crypto.randomUUID()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("isle-fund-covers")
        .upload(path, coverFile, { contentType: coverFile.type, upsert: false });
      setUploading(false);
      if (upErr) { setBusy(false); toast.error(`Upload failed: ${upErr.message}`); return; }
      const { data: pub } = supabase.storage.from("isle-fund-covers").getPublicUrl(path);
      coverUrl = pub.publicUrl;
    }

    const { data, error } = await supabase
      .from("isle_funds")
      .insert({
        creator_id: user.id,
        title: form.title.trim(),
        story: form.story.trim() || null,
        category: form.category,
        goal_cents: goalCents,
        cover_url: coverUrl,
        deadline: form.deadline ? new Date(form.deadline).toISOString() : null,
      })
      .select()
      .single();

    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Campaign published");
    nav(`/isle-fund/${data.id}`);
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="sticky top-0 z-10 backdrop-blur-md bg-background/80 border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button asChild size="icon" variant="ghost"><Link to="/isle-fund"><ArrowLeft className="size-5" /></Link></Button>
          <h1 className="text-xl font-bold">Start a campaign</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <Card>
          <CardHeader><CardTitle>Tell your story</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-5">
              {/* Cover */}
              <div className="space-y-2">
                <Label>Cover image</Label>
                {coverPreview ? (
                  <div className="relative aspect-video rounded-lg overflow-hidden border bg-muted">
                    <img src={coverPreview} alt="Cover preview" className="w-full h-full object-cover" />
                    <Button
                      type="button"
                      size="icon"
                      variant="secondary"
                      className="absolute top-2 right-2 size-8"
                      onClick={clearCover}
                      aria-label="Remove cover"
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="w-full aspect-video rounded-lg border-2 border-dashed border-border hover:border-primary hover:bg-muted/30 transition-colors flex flex-col items-center justify-center gap-2 text-muted-foreground"
                  >
                    <ImagePlus className="size-8" />
                    <span className="text-sm">Tap to add a cover image</span>
                    <span className="text-xs">JPG or PNG, up to 5 MB</span>
                  </button>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={onPickFile}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="title">Title</Label>
                  <span className={`text-xs ${form.title.length >= TITLE_MAX ? "text-destructive" : "text-muted-foreground"}`}>
                    {form.title.length}/{TITLE_MAX}
                  </span>
                </div>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Help us rebuild the studio"
                  maxLength={TITLE_MAX}
                  required
                />
                <p className="text-xs text-muted-foreground">At least {TITLE_MIN} characters.</p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="story">Story</Label>
                  <span className="text-xs text-muted-foreground">{form.story.length}/{STORY_MAX}</span>
                </div>
                <Textarea
                  id="story"
                  value={form.story}
                  onChange={(e) => setForm({ ...form, story: e.target.value })}
                  placeholder="Why does this matter? Who does it help?"
                  rows={6}
                  maxLength={STORY_MAX}
                />
                <p className="text-xs text-muted-foreground">
                  {form.story.trim().length < STORY_MIN
                    ? `${STORY_MIN - form.story.trim().length} more characters to go.`
                    : "Looks good."}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger><SelectValue placeholder="Choose a category" /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="goal">Goal (USD)</Label>
                  <Input
                    id="goal"
                    type="number"
                    min={GOAL_MIN_USD}
                    max={GOAL_MAX_USD}
                    step="1"
                    inputMode="numeric"
                    value={form.goal}
                    onChange={(e) => setForm({ ...form, goal: e.target.value })}
                    placeholder="5000"
                    required
                  />
                  <p className="text-xs text-muted-foreground">${GOAL_MIN_USD}–${GOAL_MAX_USD.toLocaleString()}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="deadline">Deadline (optional)</Label>
                <Input
                  id="deadline"
                  type="date"
                  value={form.deadline}
                  onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                  min={addDays(startOfToday(), DEADLINE_MIN_DAYS).toISOString().split("T")[0]}
                  max={addDays(startOfToday(), DEADLINE_MAX_DAYS).toISOString().split("T")[0]}
                />
                <p className="text-xs text-muted-foreground">
                  Pick a date between tomorrow and {fmtDate(addDays(startOfToday(), DEADLINE_MAX_DAYS))}.
                </p>
              </div>

              <Button type="submit" disabled={busy} className="w-full">
                {busy ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="size-4 animate-spin" />
                    {uploading ? "Uploading cover…" : "Publishing…"}
                  </span>
                ) : "Publish campaign"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
